/* eslint-disable no-confusing-arrow */

import _ from 'lodash';
import React, { PureComponent } from 'react';
import Chart from 'chart.js';
import {
  Button,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  OutlinedInput,
  LinearProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { AccountCircle, Launch } from '@mui/icons-material';
import { DesktopDatePicker } from '@mui/x-date-pickers';

import { connect } from 'react-redux';
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

import toast, { superAdminToast } from '../../../utils/toast';
import { isArrayEqual } from '../../../utils';
import { IAppState } from '../../../state/reducers';
import {
  setEndDate,
  setStartDate,
  startDateClose,
  toggleEndDate,
  queryAssetAccessLogs,
  loadOrgs,
  selectOrg,
  updateJiraTicket,
  setRetried,
  IAssetAccessLogShim,
} from '../../../state/adminDashboard';
import {
  IDispatchProps,
  IProps,
  IState,
  IStateProps,
} from './interfaces';
import {
  Container,
  DateSelect,
  OrgSelect,
  OverviewCard,
  StyledDataGrid,
  StyledGraph,
  StyledHeader,
  StyledOverview,
  StyledTable,
  ProgressContainer,
} from './styles';
import { IAsset } from '../../../api/vault/searchEngine';
import { transcodeAsset } from '../../../api/vault/assetUpload';
import {
  getAssetFromApi,
  getVideoSourceFromPreview,
} from '../../AssetDetails/api/assetsLookupsMetatags';
import { getDownloadLink } from '../../../api/vault/assetDetails';
import { selectIsUserVaultSuperAdmin } from '../../../state/user/userOrgRole';
import { VAULT_VIDEO_TYPE } from '../../../constants';

const chance = require('chance').Chance();

class AdminDashboard extends PureComponent<IProps, IState> {
  state: IState = {
    chart: null,
    pieChart: null,
  };

  handleChartUpdate() {
    const { chart, pieChart } = this.state;
    const { logs, startDate, endDate } = this.props;
    // note: we pull the dates from the logs expecting them to be the last 10 days
    const labels = _.uniq(logs.map(({ actionDate }) => actionDate.substr(0, 10)));
    // note: set labels in order
    labels.reverse();
    // note: based on date ranges specified
    const dataset_failed = labels.reduce((container, date) => {
      const entries = logs.filter(({ actionDate, actionType }) => actionDate.substr(0, 10) === date && actionType === 'Failure');
      return [...container, entries.length];
    }, []);
    // note: check to see if chart has been created
    if (chart) {
      chart.data.labels = labels;
      chart.data.datasets[0].data = dataset_failed;
      chart.update();
    } else {
      const uploadChart = document.querySelector('#uploadChart') as HTMLCanvasElement;
      const ctx = uploadChart.getContext('2d');
      const assetChart = document.querySelector('#assetChart') as HTMLCanvasElement;
      const ctx2 = assetChart.getContext('2d');
      this.setState({
        chart: new Chart(ctx, {
          type: 'line',
          data: {
            labels,
            datasets: [
              {
                label: 'Failed',
                data: dataset_failed,
                borderColor: 'red',
                backgroundColor: 'red',
                fill: false,
              },
            ],
          },
          options: {
            title: {
              display: true,
              text: 'Upload Failures',
            },
            scales: {
              xAxes: [{
                display: true,
                scaleLabel: {
                  display: true,
                  labelString: 'Date',
                },
              }],
              yAxes: [{
                display: true,
                scaleLabel: {
                  display: true,
                  labelString: 'Count',
                },
              }],
            },
          },
        }),
        // note: currently mocked with random data
        // TODO: hammer out specifics with bridget
        // TODO: add custom labels with specific issue types
        pieChart: new Chart(ctx2, {
          type: 'pie',
          data: {
            labels: [
              '*Imports',
              '*Downloads',
            ],
            datasets: [{
              label: 'Dataset 1',
              data: [...Array(2)].map((i) => chance.integer({ min: 0, max: 100 })),
              backgroundColor: [
                'cornflowerblue',
                'red',
              ],
            }],
          },
        }),
      });
    }
  }

  componentDidMount = async () => {
    const { onLoadOrgs, onQueryLogs } = this.props;
    await onLoadOrgs();
    onQueryLogs();
  };

  componentDidUpdate({ logs }: IProps) {
    // note: if mismatch in old vs new logs, trigger chart update
    const { logs: mylogs } = this.props;
    if (!isArrayEqual(logs, mylogs)) {
      this.handleChartUpdate();
    }
  }

  render() {
    const {
      startDate,
      endDate,
      onSetStartDate,
      onSetEndDate,
      onStartDateClose,
      onToggleEndDate,
      onSelectOrg,
      onUpdateJiraTicket,
      onSetRetried,
      isEndDateOpen,
      orgs,
      org,
      logs,
      isLoading,
    } = this.props;

    const overviewItems = [{
      header: 'Total Upload Failures',
      total: logs.filter(({ actionType }) => actionType === 'Failure').length,
    }, {
      header: '* Assets with issues',
      render: () => <canvas id="assetChart" width="200px" height="200px" />,
    }];

    const columns: GridColDef[] = [
      { field: 'assetName', headerName: 'Asset Name', width: 250 },
      { field: 'actionType', headerName: 'Type/Action', width: 150 },
      { field: 'actionDate', headerName: 'Date', width: 250 },
      { field: 'stepFailed', headerName: 'Step Failed', width: 250 },
      {
        field: 'action',
        headerName: 'Action',
        width: 100,
        renderCell: (params: GridRenderCellParams) => {
          const {
            row: {
              assetId,
              actionType,
              stepFailed,
              retried,
            },
          } = params;
          if (actionType !== 'Failure' || (stepFailed !== 'FailedTranscode' && stepFailed !== 'FailedToReplaceFullRendition')) {
            return null;
          }
          return (
            <Button
              variant="contained"
              color="primary"
              onClick={async () => {
                onSetRetried(params.row as IAssetAccessLogShim);
                const asset = await getAssetFromApi(assetId) as IAsset;
                const { videoType } = asset;
                let downloadLink: string = '';
                let previewLink: string = '';
                try {
                  const response = await getDownloadLink({ id: assetId }, 1, 'probe', false);
                  downloadLink = response.data.data;
                  try {
                    previewLink = await getVideoSourceFromPreview(assetId);
                  } catch {
                    previewLink = '';
                    // continue
                  }
                } catch (e) {
                  downloadLink = '';
                  // continue
                }
                if (!downloadLink || downloadLink === '' || !previewLink || previewLink === '') {
                  const needsExchangeTranscode = (!downloadLink || downloadLink === '');
                  const isVaultUpload = !videoType || videoType === VAULT_VIDEO_TYPE.Vault;
                  const transcodeResult = await transcodeAsset(assetId, needsExchangeTranscode, true, isVaultUpload);
                  superAdminToast(false, `Transcode process has been initiated for asset "${assetId}"`, { type: toast.TYPE.INFO });
                } else {
                  superAdminToast(false, `Transcode no longer required for asset "${assetId}"`, { type: toast.TYPE.INFO });
                }
              }}
              disabled={retried}
            >
              Retry
            </Button>
          );
        },
      },
      { field: 'machine', headerName: 'Machine', width: 150 },
      { field: 'progress', headerName: 'Progress', width: 150 },
      {
        field: 'ticket',
        headerName: 'Ticket',
        width: 250,
        renderCell: (params: GridRenderCellParams) => {
          const {
            row: {
              id,
              actionType,
              ticket,
            },
          } = params;
          if (actionType === 'Failure') {
            return (
              <OutlinedInput
                type="text"
                placeholder="Enter ticket URL"
                defaultValue={ticket}
                notched={false}
                endAdornment={(
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="Open Jira"
                      onClick={(e) => {
                        window.open('https://catapult-catalyst.atlassian.net/secure/CreateIssue.jspa?issuetype=10004&pid=10042', '_blank');
                      }}
                      size="large"
                    >
                      <Launch />
                    </IconButton>
                  </InputAdornment>
                )}
                onChange={(e) => {
                  onUpdateJiraTicket(params.row as IAssetAccessLogShim, e.target.value);
                }}
              />
            );
          }
          return (
            <a href={ticket}>{ticket}</a>
          );
        },
      },
    ];

    return (
      <Container>
        <ProgressContainer>
          {isLoading && <LinearProgress />}
        </ProgressContainer>
        <StyledHeader container item>
          <OrgSelect item xs={3}>
            <AccountCircle className="accountIcon" />
            <FormControl className="formControl">
              <InputLabel id="org-select-dashboard-label">Org Select</InputLabel>
              <Select
                value={org}
                labelId="org-select-dashboard-label"
                id="org-select-dashboard"
                onChange={(e) => onSelectOrg(e.target.value as string)}
              >
                <MenuItem value="">All Orgs</MenuItem>
                {orgs.map(({ id, displayName }) => <MenuItem key={id} value={displayName}>{displayName}</MenuItem>)}
              </Select>
            </FormControl>
          </OrgSelect>
          <DateSelect item xs={5}>
            <h3>Date range</h3>
            <DesktopDatePicker
              format="MM/dd/yyyy"
              // id="start-date-picker"
              label="Start Date"
              value={startDate}
              onChange={onSetStartDate}
              onClose={onStartDateClose}
              className="AdminDashboardDatePicker"
              slotProps={{
                actionBar: {
                  actions: ['clear', 'today'],
                },
              }}
              // style={{ width: '170px', marginTop: '8px', marginBottom: '8px' }}
              // KeyboardButtonProps={{
              //   'aria-label': 'Start Date',
              // }}
            />
            <DesktopDatePicker
              format="MM/dd/yyyy"
              // id="end-date-picker"
              label="End Date"
              value={endDate}
              onChange={onSetEndDate}
              onClose={() => onToggleEndDate(false)}
              onOpen={() => onToggleEndDate(true)}
              // style={{ width: '170px', marginTop: '8px', marginBottom: '8px' }}
              open={isEndDateOpen}
              slotProps={{
                actionBar: {
                  actions: ['clear', 'today'],
                },
              }}
              // KeyboardButtonProps={{
              //   'aria-label': 'End Date',
              // }}
            />
          </DateSelect>
        </StyledHeader>
        <StyledOverview
          container
          item
        >
          {overviewItems.map((item) => (
            <Grid
              item
              key={item.header}
              xs={2}
              style={{ position: 'relative' }}
            >
              <OverviewCard elevation={3}>
                <h3>{item.header}</h3>
                {item.render ? item.render() : <h1>{item.total}</h1>}
              </OverviewCard>
            </Grid>
          ))}
        </StyledOverview>
        <StyledTable
          container
          item
          style={{ flexGrow: 5 }}
        >
          <StyledDataGrid
            rows={logs}
            columns={columns}
          />
        </StyledTable>
        <StyledGraph
          container
          item
        >
          <canvas
            id="uploadChart"
            width="500"
            height="100"
          />
        </StyledGraph>
      </Container>
    );
  }
}

const mapStateToProps = (state: IAppState): IStateProps => ({
  startDate: state.adminDashboard.startDate,
  endDate: state.adminDashboard.endDate,
  isEndDateOpen: state.adminDashboard.isEndDateOpen,
  orgs: state.adminDashboard.orgs,
  org: state.adminDashboard.org,
  logs: state.adminDashboard.logs,
  isLoading: state.adminDashboard.isLoading,
  isVaultSuperAdmin: selectIsUserVaultSuperAdmin(state),
});

const mapDispatchToProps: IDispatchProps = {
  onLoadOrgs: loadOrgs,
  onSelectOrg: selectOrg,
  onQueryLogs: queryAssetAccessLogs,
  onSetStartDate: setStartDate,
  onSetEndDate: setEndDate,
  onStartDateClose: startDateClose,
  onToggleEndDate: toggleEndDate,
  onUpdateJiraTicket: updateJiraTicket,
  onSetRetried: setRetried,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(AdminDashboard);
